import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["reply", "attachments", "getDisabledProps", "maxSize", "onSubmit", "originalGenericChannelId", "auth", "threadId", "prosemirrorConfig", "isConfigLoading", "replyRecommendationsEnabled", "savePersistentDraft", "clearPersistentDraft"];
import { useEffect, useCallback, useMemo, useState, useRef, useContext } from 'react';
import { getCommonMessage, getReplyId } from 'conversations-thread-data/common-reply/public/operators/commonReplyGetters';
import { ChannelCapabilitiesConfigContext } from 'conversations-thread-view/channel-capabilities/context/ChannelCapabilitiesConfigContext';
import { getIsForward } from 'conversations-message-history/common-message-format/operators/emailMetadataGetters';
import { createInsertableReplyHistoryPlugin, commands as insertableReplyHistoryCommands } from 'hubspot-prosemirror/prosemirror/plugins/InsertableReplyHistoryPlugin';
import { fetchEmailReplyHistory } from '../clients/fetchEmailReplies';
import debounce from 'hs-lodash/debounce';
import { RichTextEditor } from '../../../prosemirror/protected/components';
import { getTracker, getTrackingContext } from '../../../usage-tracking/clients/usageTracker';
import { sendDisabledReasonToErrorMessage } from '../../../disable-send/public/operators';
import { useMessageBodyDisabledConfig } from '../hooks/useMessageBodyDisabledConfig';
import { ReplyRecommendationManager } from '../../../reply-recommendations/protected/components';
import { THREAD_COMMENT } from 'conversations-message-history/comment-message/constants/messageTypes';
import { useBaseEditorState, useEditorStateDraft, useShouldIncludeEmailReplies } from '../../../editor-state/protected/hooks';
import { editorDraftHasAnyContent, editorStateHasContent } from '../../../editor-state/protected/operators';
import { useSelector } from 'react-redux';
import { getIsUngatedForPersistedDrafts } from 'conversations-thread-data/auth/public/selectors';
import { ComposerMetadataContext } from 'composer-data/email-metadata/public/contexts';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EditorStateProvider = props => {
  const {
      reply,
      attachments,
      getDisabledProps,
      maxSize,
      onSubmit,
      originalGenericChannelId,
      auth,
      threadId,
      prosemirrorConfig,
      isConfigLoading,
      replyRecommendationsEnabled,
      savePersistentDraft,
      clearPersistentDraft
    } = props,
    restOfRTEProps = _objectWithoutPropertiesLoose(props, _excluded);
  const commonMessage = getCommonMessage(reply);
  const isUngatedForPersistedDrafts = useSelector(getIsUngatedForPersistedDrafts);
  const {
    setComposerMetadata
  } = useContext(ComposerMetadataContext);
  useEffect(() => {
    setComposerMetadata({
      reply
    });
  }, [reply, setComposerMetadata]);
  const replyId = getReplyId(reply);
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
  const [draft, save, clear] = useEditorStateDraft(replyId);
  const [editorState, setEditorState, resetEditorState] = useBaseEditorState(reply, isUngatedForPersistedDrafts ? null : draft);
  const internalEditorStateSetterRef = useRef(() => {});
  const [hasContent, setHasContent] = useState(false);
  const richTextEditorRef = useRef(null);
  const prevAttachmentsRef = useRef(attachments);
  const debouncedHandleHasContent = useMemo(() => debounce((nextEditorState, nextAttachments) => {
    const currentContent = editorStateHasContent(nextEditorState, nextAttachments);
    setHasContent(currentContent);
  }, 2000), [setHasContent]);
  const debouncedClearPersistentDraft = useMemo(() => debounce(() => clearPersistentDraft === null || clearPersistentDraft === void 0 ? void 0 : clearPersistentDraft(), 200), [clearPersistentDraft]);
  const shouldIncludeEmailReplies = useShouldIncludeEmailReplies(commonMessage);
  const [hasReplies, setHasReplies] = useState(false);
  useEffect(() => {
    if (isUngatedForPersistedDrafts && prevAttachmentsRef.current !== attachments && (attachments === null || attachments === void 0 ? void 0 : attachments.size) === 0 && !editorStateHasContent(editorState, attachments)) {
      debouncedClearPersistentDraft();
    }
    prevAttachmentsRef.current = attachments;
  }, [attachments, debouncedClearPersistentDraft, editorState, isUngatedForPersistedDrafts]);
  const handleOnEditorStateChange = useCallback(nextEditorState => {
    const previousEditorState = editorState;
    setEditorState(nextEditorState);
    if (!isUngatedForPersistedDrafts) save(nextEditorState);
    if (isUngatedForPersistedDrafts) {
      const contentChanged = !(previousEditorState !== null && previousEditorState !== void 0 && previousEditorState.doc.eq(nextEditorState.doc));
      const nextHasContent = editorStateHasContent(nextEditorState, attachments);
      if (contentChanged) {
        if (nextHasContent) {
          const hasSelection = !(previousEditorState !== null && previousEditorState !== void 0 && previousEditorState.selection.empty);
          const allTextDeleted = !editorDraftHasAnyContent(nextEditorState) && editorDraftHasAnyContent(previousEditorState);
          if (!hasSelection && allTextDeleted) {
            return;
          }
          savePersistentDraft === null || savePersistentDraft === void 0 || savePersistentDraft(reply.set('editorState', nextEditorState));
        }
        // doesn't have content, but previously did, so we clear the persistent draft
        else if (hasContent) {
          debouncedClearPersistentDraft();
        }
      }
      // Cancel any pending clearing if content is added in the next editor state
      if (nextHasContent) {
        debouncedClearPersistentDraft.cancel();
      }
    }

    // This part handles the local `hasContent` state for disabling submit button
    if (!hasContent && editorStateHasContent(nextEditorState, attachments)) {
      debouncedHandleHasContent.flush();
      setHasContent(true);
    } else {
      debouncedHandleHasContent(nextEditorState, attachments);
    }
  }, [editorState, setEditorState, save, isUngatedForPersistedDrafts, attachments, savePersistentDraft, reply, hasContent, debouncedHandleHasContent, debouncedClearPersistentDraft]);
  const handleOnSubmit = useCallback(({
    html,
    plainText,
    options
  }) => {
    // If not using the editable replies feature, we force the backend's reply history.
    // The ProsemirrorRichTextEditor component should leave this as false in this case,
    // but enforce it here to be safe.
    if (!shouldIncludeEmailReplies) {
      options.alreadyIncludesPreviousReplies = false;
    }
    onSubmit({
      html,
      plainText,
      options
    });
    resetEditorState();
    setHasContent(false);
    if (isUngatedForPersistedDrafts && clearPersistentDraft) {
      clearPersistentDraft();
    } else {
      clear();
    }
  }, [onSubmit, resetEditorState, clear, clearPersistentDraft, isUngatedForPersistedDrafts, shouldIncludeEmailReplies]);
  const {
    disabled: disableSubmit,
    sendDisabledReason,
    options
  } = getDisabledProps({
    hasContent,
    editorState,
    maxSize
  });
  const disableSubmitMessage = sendDisabledReason && sendDisabledReasonToErrorMessage(sendDisabledReason, options);
  const disabledAwaitingUserAction = disableSubmit && sendDisabledReason === 'NO_CONTENT';
  useEffect(() => {
    setHasContent(editorStateHasContent(editorState, attachments));
  }, [editorState, attachments, setHasContent]);
  useEffect(() => {
    return () => debouncedHandleHasContent.cancel();
  }, [debouncedHandleHasContent]);
  const trackingContext = getTrackingContext();
  trackingContext.originalGenericChannelId = originalGenericChannelId;
  const {
    disabledConfig
  } = useMessageBodyDisabledConfig();
  const {
    genericChannelId
  } = useContext(ChannelCapabilitiesConfigContext);
  const validThreadId = Boolean(Number(threadId));
  const shouldDisplayReplyRec = replyRecommendationsEnabled && genericChannelId !== THREAD_COMMENT && validThreadId;
  const isForward = getIsForward(commonMessage);

  // Store reply content promises as a ref so that they can be updated
  // without invalidating the plugin configuration.
  const replyContentPromises = useRef({
    replyHistoryPromise: Promise.resolve(null)
  });

  // Sync up the reply history promise values when the replied-to message ID changes.
  useEffect(() => {
    if (shouldIncludeEmailReplies) {
      replyContentPromises.current.replyHistoryPromise = fetchEmailReplyHistory(
      // @ts-expect-error InReplyToId is a string in CommonMessage
      commonMessage.inReplyToId, isForward ? 'FORWARD' : 'REPLY').then(replyHistory => {
        setHasReplies(!!replyHistory);
        return replyHistory;
      }).catch(error => {
        if (error.status === 404) {
          setHasReplies(false);
          return null;
        }
        throw error;
      });
    }
  }, [shouldIncludeEmailReplies, commonMessage, isForward]);
  const plugins = useMemo(() => {
    const result = [];
    if (shouldIncludeEmailReplies) {
      result.push(createInsertableReplyHistoryPlugin({
        autoInsert: hasReplies,
        replyFetcher: () => replyContentPromises.current.replyHistoryPromise
        /* signatureFetcher omitted for first rollout */
      }));
    }
    return result;
  }, [shouldIncludeEmailReplies, hasReplies]);

  // Detect a transition from true to false in hasReplies. This indicates that
  // we assumed there would be replies, but the backend returned an error, or
  // there were no replies after all. The button should be removed from the doc.
  useEffect(() => {
    if (hasReplies) {
      var _richTextEditorRef$cu, _richTextEditorRef$cu2;
      const {
        state,
        dispatch
      } = (_richTextEditorRef$cu = (_richTextEditorRef$cu2 = richTextEditorRef.current) === null || _richTextEditorRef$cu2 === void 0 ? void 0 : _richTextEditorRef$cu2.view) !== null && _richTextEditorRef$cu !== void 0 ? _richTextEditorRef$cu : {};
      if (state && dispatch) {
        insertableReplyHistoryCommands.insertReplyButton(state, dispatch);
      }
    }
  }, [hasReplies]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldDisplayReplyRec ? /*#__PURE__*/_jsx(ReplyRecommendationManager, {
      editorState: editorState,
      setEditorState: internalEditorStateSetterRef.current,
      threadId: threadId
    }) : null, /*#__PURE__*/_jsx(RichTextEditor, Object.assign({
      auth: auth,
      editorState: editorState,
      setEditorState: handleOnEditorStateChange,
      attachments: attachments,
      shouldDisableSubmit: disableSubmit,
      disabledConfig: disabledConfig,
      disableSubmitMessage: disableSubmitMessage,
      disabledAwaitingUserAction: disabledAwaitingUserAction,
      prosemirrorConfig: prosemirrorConfig,
      editorRef: richTextEditorRef,
      onSubmit: handleOnSubmit,
      tracker: getTracker(),
      trackingContext: trackingContext,
      canUseProsemirror: true,
      internalEditorStateSetterRef: internalEditorStateSetterRef,
      isConfigLoading: isConfigLoading,
      plugins: plugins
    }, restOfRTEProps))]
  });
};
EditorStateProvider.displayName = 'EditorStateProvider';
export default EditorStateProvider;